import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";
import SaveIcon from "@mui/icons-material/Save";
import { useDispatch, useSelector } from "react-redux";
import api from "lib/api/api";
import { fetchAudioQuestions, fetchLesenPassage, fetchMcqQuestions } from "../../../redux/Actions/einstufungActions";
import TeacherNavbar from "./TeacherNavbar";
import McqTab from "./ManageEinstufungstest/McqTab";
import LesenTab from "./ManageEinstufungstest/LesenTab";
import HoerenTab from "./ManageEinstufungstest/HoerenTab";
import "./CvManagementPage.css";

const LEVELS = ["A2", "B1", "B2", "C1"];
const TEILE = ["A", "B", "C"];
const RESULT_STATUSES = ["PASS", "PASS_WITH_RECOMMENDATION", "CONDITIONAL_REVIEW", "FAIL"];
const CSV_HEADERS = [
  "targetLevel",
  "teil",
  "category",
  "passageKey",
  "passageTitle",
  "passageText",
  "questionText",
  "optionA",
  "optionB",
  "optionC",
  "optionD",
  "correctOption",
  "mark",
  "orderNumber",
  "active",
];

const csvTemplate = `${CSV_HEADERS.join(",")}\nA2,A,Grammatik,,,"Lea schreibt ihrer Freundin. Du arbeitest jetzt schon seit einigen Wochen in der neuen Firma.",Hast du _____ neuen Kollegen schon kennengelernt?,deine,meine,ihre,,A,1,1,TRUE\nA2,A,Grammatik,,,"Jonas erzählt seinem Bruder, wo er gerade wohnt.",Ich wohne bei _____ Eltern. Die Wohnung ist groß.,meine,meinen,meiner,,C,1,2,TRUE\nA2,C,Situationen,a2_situation_01,Im Sprachkurs,"Maria besucht einen Deutschkurs.",Wo ist Maria?,Im Kurs,Im Bahnhof,Im Büro,Im Park,A,1,1,TRUE\nA2,C,Situationen,a2_situation_01,,,Warum lernt Maria Deutsch?,Für die Arbeit,Für Urlaub,Für Sport,Für Musik,A,1,2,TRUE\nA2,C,Situationen,a2_situation_01,,,Wann besucht Maria den Kurs?,Am Morgen,Am Abend,Am Sonntag,Im Sommer,B,1,3,TRUE`;
const templateRows = [
  {
    targetLevel: "A2",
    teil: "A",
    category: "Grammatik",
    passageKey: "",
    passageTitle: "",
    passageText: "Lea schreibt ihrer Freundin. Du arbeitest jetzt schon seit einigen Wochen in der neuen Firma.",
    questionText: "Hast du _____ neuen Kollegen schon kennengelernt?",
    optionA: "deine",
    optionB: "meine",
    optionC: "ihre",
    optionD: "",
    correctOption: "A",
    mark: "1",
    orderNumber: "1",
    active: "TRUE",
  },
  {
    targetLevel: "A2",
    teil: "A",
    category: "Grammatik",
    passageKey: "",
    passageTitle: "",
    passageText: "Jonas erzählt seinem Bruder, wo er gerade wohnt.",
    questionText: "Ich wohne bei _____ Eltern. Die Wohnung ist groß.",
    optionA: "meine",
    optionB: "meinen",
    optionC: "meiner",
    optionD: "",
    correctOption: "C",
    mark: "1",
    orderNumber: "2",
    active: "TRUE",
  },
  {
    targetLevel: "A2",
    teil: "C",
    category: "Situationen",
    passageKey: "a2_situation_01",
    passageTitle: "Im Sprachkurs",
    passageText: "Maria besucht einen Deutschkurs.",
    questionText: "Wo ist Maria?",
    optionA: "Im Kurs",
    optionB: "Im Bahnhof",
    optionC: "Im Büro",
    optionD: "Im Park",
    correctOption: "A",
    mark: "1",
    orderNumber: "1",
    active: "TRUE",
  },
  {
    targetLevel: "A2",
    teil: "C",
    category: "Situationen",
    passageKey: "a2_situation_01",
    passageTitle: "",
    passageText: "",
    questionText: "Warum lernt Maria Deutsch?",
    optionA: "Für die Arbeit",
    optionB: "Für Urlaub",
    optionC: "Für Sport",
    optionD: "Für Musik",
    correctOption: "A",
    mark: "1",
    orderNumber: "2",
    active: "TRUE",
  },
  {
    targetLevel: "A2",
    teil: "C",
    category: "Situationen",
    passageKey: "a2_situation_01",
    passageTitle: "",
    passageText: "",
    questionText: "Wann besucht Maria den Kurs?",
    optionA: "Am Morgen",
    optionB: "Am Abend",
    optionC: "Am Sonntag",
    optionD: "Im Sommer",
    correctOption: "B",
    mark: "1",
    orderNumber: "3",
    active: "TRUE",
  },
];const ManageEinstufungstest = () => {
  const dispatch = useDispatch();
  const oldPlacementLoading = useSelector((state) => state.einstufung.loading);
  const [mode, setMode] = useState("new");
  const [oldTabIndex, setOldTabIndex] = useState(0);
  const [level, setLevel] = useState("A2");
  const [teil, setTeil] = useState("A");
  const [parts, setParts] = useState([]);
  const [rules, setRules] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [templatePreviewOpen, setTemplatePreviewOpen] = useState(false);
  const fileInputRef = useRef(null);

  const selectedPart = useMemo(() => parts.find((p) => p.teil === teil), [parts, teil]);

  useEffect(() => {
    if (mode !== "old") return;
    if (oldTabIndex === 0) dispatch(fetchMcqQuestions(0));
    if (oldTabIndex === 1) dispatch(fetchLesenPassage());
    if (oldTabIndex === 2) dispatch(fetchAudioQuestions(0));
  }, [dispatch, mode, oldTabIndex]);

  const loadAll = async () => {
    setLoading(true);
    setMessage(null);
    try {
      const [partsRes, rulesRes, questionsRes] = await Promise.all([
        api.get("/placement-questions/parts", { params: { targetLevel: level } }),
        api.get("/placement-questions/evaluation-rules", { params: { targetLevel: level } }),
        api.get("/placement-questions", { params: { targetLevel: level, teil, page: 0, size: 100 } }),
      ]);
      setParts(partsRes.data || []);
      setRules(rulesRes.data || []);
      setQuestions(questionsRes.data?.content || []);
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err.message || "Failed to load placement test management data" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, teil]);

  const updatePart = (teilValue, patch) => {
    setParts((prev) => prev.map((p) => (p.teil === teilValue ? { ...p, ...patch } : p)));
  };

  const saveParts = async () => {
    try {
      const { data } = await api.put("/placement-questions/parts", parts, { params: { targetLevel: level } });
      setParts(data || []);
      setMessage({ type: "success", text: "Part visibility saved." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err.message || "Failed to save parts" });
    }
  };

  const updateRule = (index, patch) => {
    setRules((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const saveRules = async () => {
    try {
      const payload = rules.map((r) => ({ ...r, targetLevel: level, active: true }));
      const { data } = await api.put("/placement-questions/evaluation-rules", payload, { params: { targetLevel: level } });
      setRules(data || []);
      setMessage({ type: "success", text: "Evaluation rules saved." });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err.message || "Failed to save evaluation rules" });
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([csvTemplate], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "placement-question-template.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const previewImport = async (file) => {
    if (!file) return;
    setSelectedFile(file);
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/placement-questions/import/preview", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      setMessage(data.valid ? { type: "success", text: `Preview valid: ${data.validRows} rows.` } : { type: "warning", text: `Preview has ${data.invalidRows} invalid rows.` });
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err.message || "Preview failed" });
    }
  };

  const confirmImport = async () => {
    if (!selectedFile) return;
    const form = new FormData();
    form.append("file", selectedFile);
    try {
      const { data } = await api.post("/placement-questions/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      if (data.valid) {
        setMessage({ type: "success", text: `Imported ${data.validRows} questions.` });
        await loadAll();
      } else {
        setMessage({ type: "warning", text: "Import blocked because the file has validation errors." });
      }
    } catch (err) {
      setMessage({ type: "error", text: err?.response?.data?.message || err.message || "Import failed" });
    }
  };

  return (
    <div className="cv-management-layout">
      <TeacherNavbar />
      <Box className="cv-management-container">
        <Typography variant="h4" gutterBottom className="einstufung-header">
          Placement Test Management
        </Typography>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <Button
            variant={mode === "new" ? "contained" : "outlined"}
            onClick={() => setMode("new")}
          >
            New Placement Test
          </Button>
          <Button
            variant={mode === "old" ? "contained" : "outlined"}
            color="warning"
            onClick={() => setMode("old")}
          >
            Old Placement Test Backup
          </Button>
        </Stack>

        {message && <Alert severity={message.type} sx={{ mb: 2 }}>{message.text}</Alert>}

        {mode === "old" ? (
          <Card sx={{ flex: 1, overflow: "auto" }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ p: 2 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Backup view for the previous placement test. This keeps the old MCQ, Lesen, and HÃƒÆ’Ã‚Â¶ren/audio upload workflow available.
                </Alert>
                <Tabs value={oldTabIndex} onChange={(_, value) => setOldTabIndex(value)} sx={{ mb: 2 }}>
                  <Tab label="MCQ" />
                  <Tab label="Lesen" />
                  <Tab label="HÃƒÆ’Ã‚Â¶ren / Audio" />
                </Tabs>
              </Box>

              {oldPlacementLoading ? (
                <Box p={3}><CircularProgress /></Box>
              ) : oldTabIndex === 0 ? (
                <McqTab setMessage={(next) => setMessage({ type: "success", text: next?.message || next || "Saved." })} />
              ) : oldTabIndex === 1 ? (
                <LesenTab />
              ) : (
                <HoerenTab />
              )}
            </CardContent>
          </Card>
        ) : (
          <>

        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Target level</InputLabel>
            <Select label="Target level" value={level} onChange={(e) => setLevel(e.target.value)}>
              {LEVELS.map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Teil</InputLabel>
            <Select label="Teil" value={teil} onChange={(e) => setTeil(e.target.value)}>
              {TEILE.map((t) => <MenuItem key={t} value={t}>Teil {t}</MenuItem>)}
            </Select>
          </FormControl>
        </Stack>

        {loading ? <CircularProgress /> : (
          <Stack spacing={3}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Part visibility</Typography>
                <Grid container spacing={2}>
                  {parts.map((part) => (
                    <Grid item xs={12} md={4} key={part.teil}>
                      <Paper sx={{ p: 2 }}>
                        <FormControlLabel
                          control={<Checkbox checked={!!part.enabled} onChange={(e) => updatePart(part.teil, { enabled: e.target.checked })} />}
                          label={`Teil ${part.teil}`}
                        />
                        <TextField
                          label="Label"
                          value={part.label || ""}
                          onChange={(e) => updatePart(part.teil, { label: e.target.value })}
                          fullWidth
                          size="small"
                          sx={{ mt: 1 }}
                        />
                        <TextField
                          label="Order"
                          type="number"
                          value={part.orderNumber || 0}
                          onChange={(e) => updatePart(part.teil, { orderNumber: Number(e.target.value) })}
                          fullWidth
                          size="small"
                          sx={{ mt: 1 }}
                        />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
                <Button startIcon={<SaveIcon />} variant="contained" sx={{ mt: 2 }} onClick={saveParts}>Save parts</Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Evaluation rules ({level})</Typography>
                <Stack spacing={2}>
                  {rules.map((rule, index) => (
                    <Grid container spacing={1} key={rule.id || index} alignItems="center">
                      <Grid item xs={6} md={1.2}><TextField label="Min" type="number" size="small" value={rule.minScore ?? 0} onChange={(e) => updateRule(index, { minScore: Number(e.target.value) })} /></Grid>
                      <Grid item xs={6} md={1.2}><TextField label="Max" type="number" size="small" value={rule.maxScore ?? 0} onChange={(e) => updateRule(index, { maxScore: Number(e.target.value) })} /></Grid>
                      <Grid item xs={12} md={2.2}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select label="Status" value={rule.resultStatus || "PASS"} onChange={(e) => updateRule(index, { resultStatus: e.target.value })}>
                            {RESULT_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={1.4}>
                        <FormControl size="small" fullWidth>
                          <InputLabel>Final</InputLabel>
                          <Select label="Final" value={rule.finalLevel || level} onChange={(e) => updateRule(index, { finalLevel: e.target.value })}>
                            {["A1", ...LEVELS].map((l) => <MenuItem key={l} value={l}>{l}</MenuItem>)}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={6} md={1.4}>
                        <FormControlLabel control={<Checkbox checked={!!rule.allowBooking} onChange={(e) => updateRule(index, { allowBooking: e.target.checked })} />} label="Book" />
                      </Grid>
                      <Grid item xs={12} md={4.6}>
                        <Stack spacing={1}>
                          <TextField
                            label="Student message EN"
                            size="small"
                            fullWidth
                            value={rule.studentMessageEn || rule.studentMessage || ""}
                            onChange={(e) => updateRule(index, {
                              studentMessageEn: e.target.value,
                              studentMessage: e.target.value,
                              teacherMessage: e.target.value,
                            })}
                          />
                          <TextField
                            label="Student message DE"
                            size="small"
                            fullWidth
                            value={rule.studentMessageDe || ""}
                            onChange={(e) => updateRule(index, { studentMessageDe: e.target.value })}
                          />
                          <TextField
                            label="Student message AR"
                            size="small"
                            fullWidth
                            value={rule.studentMessageAr || ""}
                            onChange={(e) => updateRule(index, { studentMessageAr: e.target.value })}
                            inputProps={{ dir: "rtl" }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  ))}
                </Stack>
                <Button startIcon={<SaveIcon />} variant="contained" sx={{ mt: 2 }} onClick={saveRules}>Save rules</Button>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Question import</Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
                  <Button variant="outlined" onClick={() => setTemplatePreviewOpen(true)}>Preview template</Button>
                  <Button startIcon={<DownloadIcon />} variant="outlined" onClick={downloadTemplate}>Download CSV template</Button>
                  <input type="file" accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel" hidden ref={fileInputRef} onChange={(e) => previewImport(e.target.files?.[0])} />
                  <Button startIcon={<UploadIcon />} variant="outlined" onClick={() => fileInputRef.current?.click()}>Upload CSV/XLSX / Preview</Button>
                  <Button variant="contained" disabled={!preview?.valid || !selectedFile} onClick={confirmImport}>Confirm import</Button>
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Accepted formats: .csv, .xlsx, .xls. XLSX files should use the same first-row column names shown in the template.
                </Typography>
                {preview && (
                  <Box sx={{ maxHeight: 300, overflow: "auto" }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      Rows: {preview.totalRows}, Valid: {preview.validRows}, Invalid: {preview.invalidRows}
                    </Typography>
                    <Table size="small">
                      <TableHead><TableRow><TableCell>Row</TableCell><TableCell>Level</TableCell><TableCell>Teil</TableCell><TableCell>Question</TableCell><TableCell>Errors</TableCell></TableRow></TableHead>
                      <TableBody>
                        {(preview.rows || []).slice(0, 50).map((row) => (
                          <TableRow key={row.rowNumber}>
                            <TableCell>{row.rowNumber}</TableCell><TableCell>{row.targetLevel}</TableCell><TableCell>{row.teil}</TableCell><TableCell>{row.questionText}</TableCell><TableCell>{(row.errors || []).join(", ")}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Questions: {level} / Teil {teil} {selectedPart ? `(${selectedPart.label})` : ""}</Typography>
                <Table>
                  <TableHead>
                    <TableRow><TableCell>Order</TableCell><TableCell>Intro / passageText</TableCell><TableCell>Question</TableCell><TableCell>Options</TableCell><TableCell>Correct</TableCell><TableCell>Mark</TableCell><TableCell>Active</TableCell></TableRow>
                  </TableHead>
                  <TableBody>
                    {questions.map((q) => (
                      <TableRow key={q.id}>
                        <TableCell>{q.orderNumber}</TableCell>
                        <TableCell>{q.passageText}</TableCell>
                        <TableCell>{q.questionText}</TableCell>
                        <TableCell>{q.options?.map((o, i) => <div key={i}>{o}</div>)}</TableCell>
                        <TableCell>{q.correctAnswer}</TableCell>
                        <TableCell>{q.mark}</TableCell>
                        <TableCell>{q.active ? "Yes" : "No"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Dialog open={templatePreviewOpen} onClose={() => setTemplatePreviewOpen(false)} maxWidth="xl" fullWidth>
              <DialogTitle>Placement import template preview</DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Use these exact headers in row 1. For Teil A/B, use passageText for the normal intro sentence and questionText for the bold actual question. For Teil C, repeat the same passageKey for all questions belonging to the same passage; passageTitle and passageText are only required once on the first row for a new passageKey.
                </Typography>
                <Box sx={{ overflow: "auto" }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {CSV_HEADERS.map((header) => (
                          <TableCell key={header} sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>{header}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {templateRows.map((row, index) => (
                        <TableRow key={index}>
                          {CSV_HEADERS.map((header) => (
                            <TableCell key={header} sx={{ whiteSpace: "nowrap" }}>{row[header]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={downloadTemplate} startIcon={<DownloadIcon />}>Download CSV template</Button>
                <Button onClick={() => setTemplatePreviewOpen(false)}>Close</Button>
              </DialogActions>
            </Dialog>
          </Stack>
        )}
          </>
        )}
      </Box>
    </div>
  );
};

export default ManageEinstufungstest;

